import { CTAButtonLink } from "@/components/shared/CTAButtonLink";
import type { MegaMenuCTAConfig } from "@/lib/navigation-utils";
import type { PolymorphicRelationship } from "@/lib/url-utils";
import type { Page } from "@/payload-types";

interface MegaMenuCTAProps {
  config?: MegaMenuCTAConfig;
  onNavigate?: () => void;
}

// Helper to resolve link URL from polymorphic relationship
function resolveLinkUrl(link?: Page | PolymorphicRelationship | null): string {
  if (!link) return "/";

  // Handle polymorphic relationship format
  if ("relationTo" in link && "value" in link) {
    const { relationTo, value } = link;
    // Value could be ID (string/number) or object
    if (typeof value !== "object" || value === null) return "/";
    if (relationTo === "pages" && "slug" in value) {
      return value.slug === "/" ? "/" : `/${value.slug}`;
    }
  }

  // Handle direct page reference
  if ("slug" in link) {
    return link.slug === "/" ? "/" : `/${link.slug}`;
  }

  return "/";
}

export function MegaMenuCTA({ config, onNavigate }: MegaMenuCTAProps) {
  // Use CMS values with fallbacks
  const eyebrow = config?.eyebrow || "Popular Choice";
  const heading = config?.heading || "Featured Content";
  const description = config?.description || "Discover our most important content, hand-picked to help visitors find what matters most.";
  const buttonLabel = config?.buttonLabel || "Learn More";
  const linkUrl = resolveLinkUrl(config?.link);

  return (
    <div className="bg-gray-100 p-8 h-full flex flex-col justify-center">
      <span className="text-sm font-medium text-brand-dark uppercase tracking-wide">
        {eyebrow}
      </span>
      <h3 className="font-subheading text-2xl text-brand-dark mt-2">
        {heading}
      </h3>
      <p className="text-gray-700 mt-3 text-sm">
        {description}
      </p>
      <CTAButtonLink
        label={buttonLabel}
        href={linkUrl}
        onClick={onNavigate}
        className="mt-6 w-fit"
      />
    </div>
  );
}
